import { useEffect, useState, useSyncExternalStore } from 'react';

export const PHONE_QUERY = '(max-width: 767px)';
export const ADAPTIVE_QUERY = '(max-width: 1279px)';
/**
 * Touch sizing applies to every adaptive width and to any device whose
 * PRIMARY pointer is coarse (an iPad at a desktop-sized viewport). A
 * touch-screen laptop's primary pointer is the trackpad, so it keeps desktop
 * density. The CSS twin lives in styles/responsive.css and
 * responsive.guard.test.ts pins the two together.
 */
export const TOUCH_QUERY = '(max-width: 1279px), (pointer: coarse)';
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (notify) => {
      const media = window.matchMedia?.(query);
      media?.addEventListener('change', notify);
      return () => media?.removeEventListener('change', notify);
    },
    () => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches,
    () => false,
  );
}
export const usePhone = () => useMediaQuery(PHONE_QUERY);
export const useAdaptive = () => useMediaQuery(ADAPTIVE_QUERY);
export const useTouch = () => useMediaQuery(TOUCH_QUERY);

/**
 * The element matching `selector` once it exists, for a control that portals
 * itself into a slot another component renders (the View picker into the
 * phone filter bar). Watches the document until the slot appears or `enabled`
 * turns off, so render order between the two components does not matter.
 */
export function useSlot(selector: string, enabled: boolean): HTMLElement | null | undefined {
  // `undefined` until the first lookup has run, so a caller can hold its
  // fallback rendering instead of flashing it for one frame; `null` once the
  // document has been searched and holds no slot.
  const [slot, setSlot] = useState<HTMLElement | null | undefined>(enabled ? undefined : null);
  useEffect(() => {
    if (!enabled) {
      setSlot(null);
      return;
    }
    const find = () => {
      const found = document.querySelector<HTMLElement>(selector);
      setSlot((current) => (current === found ? current : found));
    };
    find();
    const observer = new MutationObserver(find);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [selector, enabled]);
  return slot;
}
